// Deferred-2 — Search autocomplete dropdown.
//
// Hooks every <input type="search" name="q"> on the page (header + hero).
// On keystroke (>=2 chars, debounced 220ms): fetches /api/search?q=…,
// renders a categorized dropdown panel attached just below the input.
//
// Features:
//   - Recent searches in localStorage (last 5).
//   - Keyboard navigation (arrow up/down, enter, escape).
//   - Categorized result rendering (products + categories matching).
//   - Auto-categorize: if query matches one of the 17 nav category slugs,
//     show the category as a separate "Categorii" section above products.
//
// No external deps. ~200 LOC.

const STORAGE_KEY = "vl-recent-searches";
const MAX_RECENT = 5;
const DEBOUNCE_MS = 220;
const MIN_CHARS = 2;

// 17 nav categories (slug, label) — kept in sync with __init__.py
// _NAV_PRODUCT_CATEGORIES. Used for the "Categorii" section in the
// dropdown. Inlined to avoid an extra round-trip.
const NAV_CATS: Array<[string, string]> = [
  ["incaltaminte-medicala",       "Încălțăminte medicală"],
  ["dispozitive-de-mers",         "Dispozitive de mers"],
  ["acid-hyaluronic",             "Acid Hyaluronic"],
  ["aparate-de-masura-medicale",  "Aparate de masură medicale"],
  ["recuperare-reabilitare",      "Recuperare, reabilitare"],
  ["ingrijirea-pacientului",      "Îngrijirea pacientului"],
  ["ingrijirea-stomei",           "Îngrijirea stomei"],
  ["incontinenta-adulti",         "Incontinența adulți"],
  ["incontinenta-urinara",        "Incontinența urinară"],
  ["mama-si-copilul",             "Mama și copilul"],
  ["proteze-de-san",              "Proteze de sân"],
  ["dopuri-de-urechi",            "Dopuri de Urechi"],
  ["echipamente-medicale",        "Echipamente medicale"],
  ["consumabile",                 "Consumabile"],
  ["perne-medicinale",            "Perne medicinale"],
  ["accesorii",                   "Accesorii"],
  ["altele",                      "Altele"],
];

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, MAX_RECENT).filter(s => typeof s === "string") : [];
  } catch { return []; }
}

function pushRecent(q: string) {
  q = q.trim();
  if (q.length < MIN_CHARS) return;
  const cur = readRecent().filter(x => x.toLowerCase() !== q.toLowerCase());
  cur.unshift(q);
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cur.slice(0, MAX_RECENT))); } catch {}
}

function escHTML(s: string): string {
  return String(s).replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c] as string));
}

function matchCategories(q: string): Array<[string, string]> {
  const ql = q.toLowerCase();
  return NAV_CATS.filter(([slug, label]) =>
    slug.includes(ql) || label.toLowerCase().includes(ql)
  ).slice(0, 4);
}

interface SearchResult {
  cod_vital: string;
  denumire: string;
  image_url?: string;
  price?: number;
  url: string;
}

class Autocomplete {
  private input: HTMLInputElement;
  private dropdown: HTMLDivElement;
  private timer: number | null = null;
  private results: SearchResult[] = [];
  private cats: Array<[string, string]> = [];
  private active = -1;  // index of the currently highlighted item across both sections

  constructor(input: HTMLInputElement) {
    this.input = input;
    this.input.setAttribute("autocomplete", "off");
    this.input.setAttribute("aria-autocomplete", "list");
    this.input.setAttribute("aria-expanded", "false");
    this.input.setAttribute("role", "combobox");

    this.dropdown = document.createElement("div");
    this.dropdown.className = "v2-autocomplete";
    this.dropdown.hidden = true;
    this.dropdown.setAttribute("role", "listbox");

    // Anchor the dropdown to the input's parent (relative-positioned).
    const parent = this.input.parentElement!;
    if (getComputedStyle(parent).position === "static") {
      parent.style.position = "relative";
    }
    parent.appendChild(this.dropdown);

    this.bind();
  }

  bind() {
    this.input.addEventListener("focus", () => this.onFocus());
    this.input.addEventListener("input", () => this.onInput());
    this.input.addEventListener("keydown", (e) => this.onKey(e));
    document.addEventListener("click", (e) => {
      if (!this.dropdown.contains(e.target as Node) && e.target !== this.input) {
        this.hide();
      }
    });
    // Persist on enter / submit.
    this.input.form?.addEventListener("submit", () => pushRecent(this.input.value));
  }

  onFocus() {
    if (this.input.value.trim().length >= MIN_CHARS) this.onInput();
    else this.renderRecent();
  }

  onInput() {
    if (this.timer) window.clearTimeout(this.timer);
    const q = this.input.value.trim();
    if (q.length < MIN_CHARS) { this.renderRecent(); return; }
    this.timer = window.setTimeout(() => this.fetch(q), DEBOUNCE_MS);
  }

  async fetch(q: string) {
    try {
      const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin",
      });
      if (!res.ok) { this.hide(); return; }
      const data = await res.json();
      this.results = (data.results || []).slice(0, 8);
      this.cats = matchCategories(q);
      this.active = -1;
      this.renderResults(q);
    } catch {
      this.hide();
    }
  }

  renderRecent() {
    const recent = readRecent();
    if (recent.length === 0) { this.hide(); return; }
    let html = `<div class="v2-autocomplete__section">
      <div class="v2-autocomplete__heading">Căutări recente</div>`;
    recent.forEach((q, i) => {
      html += `<a class="v2-autocomplete__item" data-idx="${i}" data-recent="${escHTML(q)}" href="/produse/?q=${encodeURIComponent(q)}">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        <span>${escHTML(q)}</span>
      </a>`;
    });
    html += `</div>`;
    this.dropdown.innerHTML = html;
    this.show();
  }

  renderResults(q: string) {
    if (this.results.length === 0 && this.cats.length === 0) {
      this.dropdown.innerHTML = `<div class="v2-autocomplete__empty">
        Niciun rezultat pentru <strong>${escHTML(q)}</strong>
      </div>`;
      this.show();
      return;
    }
    let html = "";
    let idx = 0;

    if (this.cats.length > 0) {
      html += `<div class="v2-autocomplete__section">
        <div class="v2-autocomplete__heading">Categorii</div>`;
      this.cats.forEach(([slug, label]) => {
        html += `<a class="v2-autocomplete__item" data-idx="${idx++}" href="/produse/${slug}/">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/></svg>
          <span>${escHTML(label)}</span>
        </a>`;
      });
      html += `</div>`;
    }

    if (this.results.length > 0) {
      html += `<div class="v2-autocomplete__section">
        <div class="v2-autocomplete__heading">Produse</div>`;
      this.results.forEach((r) => {
        const priceStr = r.price != null ? `${r.price.toFixed(2).replace(".", ",")} lei` : "";
        const imgStr = r.image_url
          ? `<img src="${escHTML(r.image_url)}" alt="" loading="lazy">`
          : `<div class="v2-autocomplete__no-img">📦</div>`;
        html += `<a class="v2-autocomplete__item v2-autocomplete__item--prod" data-idx="${idx++}" href="${escHTML(r.url)}">
          <div class="v2-autocomplete__img">${imgStr}</div>
          <div class="v2-autocomplete__prod-body">
            <div class="v2-autocomplete__name">${escHTML(r.denumire)}</div>
            <div class="v2-autocomplete__sku">Cod: ${escHTML(r.cod_vital)}</div>
          </div>
          ${priceStr ? `<div class="v2-autocomplete__price">${priceStr}</div>` : ""}
        </a>`;
      });
      html += `</div>`;
    }

    html += `<div class="v2-autocomplete__footer">
      <a href="/produse/?q=${encodeURIComponent(q)}">Vezi toate rezultatele pentru "${escHTML(q)}" &rarr;</a>
    </div>`;

    this.dropdown.innerHTML = html;
    this.show();
  }

  onKey(e: KeyboardEvent) {
    const items = this.dropdown.querySelectorAll<HTMLElement>(".v2-autocomplete__item");
    if (e.key === "Escape") { this.hide(); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      this.active = Math.min(items.length - 1, this.active + 1);
      this.highlight();
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      this.active = Math.max(-1, this.active - 1);
      this.highlight();
      return;
    }
    if (e.key === "Enter" && this.active >= 0 && items[this.active]) {
      e.preventDefault();
      pushRecent(this.input.value);
      window.location.href = items[this.active].getAttribute("href") || "/";
    }
  }

  highlight() {
    const items = this.dropdown.querySelectorAll<HTMLElement>(".v2-autocomplete__item");
    items.forEach((el, i) => el.classList.toggle("is-active", i === this.active));
    if (this.active >= 0 && items[this.active]) {
      items[this.active].scrollIntoView({ block: "nearest" });
    }
  }

  show() {
    this.dropdown.hidden = false;
    this.input.setAttribute("aria-expanded", "true");
  }
  hide() {
    this.dropdown.hidden = true;
    this.input.setAttribute("aria-expanded", "false");
    this.active = -1;
  }
}

function attach() {
  document.querySelectorAll<HTMLInputElement>(
    'input[type="search"][name="q"]'
  ).forEach((input) => {
    new Autocomplete(input);
  });
}

if (document.readyState !== "loading") attach();
else document.addEventListener("DOMContentLoaded", attach);
